/**
 * CR320: Rollout Strategy Card
 * 
 * Reusable rollout management section for Production Tools.
 * Currently houses V1 → V2 deal card rollout controls.
 * 
 * Designed to be extensible for future production rollouts.
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ToggleLeft,
  ToggleRight,
  Users,
  Percent,
  Power,
  Bug,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Eye,
  Rocket,
  ListChecks,
  Lightbulb,
  Layers,
  CreditCard,
  LayoutGrid,
  Info,
} from "lucide-react";
import {
  fetchRolloutConfig,
  updateRolloutConfig,
  getSessionId,
  getSessionBucket,
  type DealCardV2RolloutConfig,
  type RolloutMode,
  DEFAULT_ROLLOUT_CONFIG,
} from "@/lib/deal-cards/rolloutConfig";
import { computeV2ReadinessGate } from "@/lib/deal-cards/helpers/debugGuard";
import { toast } from "sonner";

// ================================================================
// RAMP CHECKLIST COMPONENT
// ================================================================

interface RampStep {
  label: string;
  mode: RolloutMode;
  percent?: number;
  description: string;
}

const RAMP_STEPS: RampStep[] = [
  { label: "Step 1", mode: "allowlist", description: "ALLOWLIST (you only)" },
  { label: "Step 2", mode: "percent", percent: 1, description: "1% of sessions" },
  { label: "Step 3", mode: "percent", percent: 5, description: "5% of sessions" },
  { label: "Step 4", mode: "percent", percent: 10, description: "10% of sessions" },
  { label: "Step 5", mode: "percent", percent: 25, description: "25% of sessions" },
  { label: "Step 6", mode: "percent", percent: 50, description: "50% of sessions" },
  { label: "Step 7", mode: "on", description: "100% (ON)" },
];

function RampChecklist({ 
  currentMode, 
  currentPercent, 
  enabled,
  onSetMode, 
  saving 
}: { 
  currentMode: RolloutMode;
  currentPercent: number;
  enabled: boolean;
  onSetMode: (mode: RolloutMode, percent?: number) => void;
  saving: boolean;
}) {
  const getCurrentStepIndex = () => {
    if (!enabled || currentMode === "off") return -1;
    if (currentMode === "on") return RAMP_STEPS.length - 1;
    if (currentMode === "allowlist") return 0;
    if (currentMode === "percent") {
      for (let i = RAMP_STEPS.length - 1; i >= 0; i--) {
        const step = RAMP_STEPS[i];
        if (step.mode === "percent" && step.percent !== undefined && currentPercent >= step.percent) {
          return i;
        }
      }
    }
    return -1;
  };
  
  const currentStepIndex = getCurrentStepIndex();
  
  return (
    <div className="space-y-1.5">
      {RAMP_STEPS.map((step, index) => {
        const isComplete = index < currentStepIndex;
        const isCurrent = index === currentStepIndex;
        const isNext = index === currentStepIndex + 1;
        
        return (
          <div 
            key={step.label}
            className={`flex items-center gap-2 p-1.5 rounded text-xs ${
              isCurrent ? "bg-primary/10 border border-primary/30" :
              isComplete ? "bg-muted/50" : ""
            }`}
          >
            <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${
              isComplete ? "bg-primary text-primary-foreground" :
              isCurrent ? "bg-primary text-primary-foreground" :
              "bg-muted text-muted-foreground"
            }`}>
              {isComplete ? "✓" : index + 1}
            </div>
            <span className={`flex-1 ${isCurrent ? "font-medium" : ""}`}>
              {step.description}
            </span>
            {isNext && (
              <Button
                size="sm"
                variant="outline"
                className="h-5 text-[10px] px-2"
                onClick={() => onSetMode(step.mode, step.percent)}
                disabled={saving}
              >
                Apply
              </Button>
            )}
            {isCurrent && (
              <Badge variant="outline" className="text-[10px] h-4">Current</Badge>
            )}
          </div>
        );
      })}
      
      <div className="pt-2 text-[10px] text-muted-foreground space-y-1">
        <p><strong>At each step, verify:</strong></p>
        <ul className="list-disc list-inside space-y-0.5 ml-1">
          <li>Render events appear in telemetry</li>
          <li>Claim/view clicks still work</li>
          <li>No console errors or visual regressions</li>
        </ul>
      </div>
    </div>
  );
}

// ================================================================
// READINESS GATE DISPLAY
// ================================================================

function ReadinessGateDisplay() {
  const gate = computeV2ReadinessGate();
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Readiness Gate</span>
        <Badge 
          variant={gate.overallReady ? "default" : "destructive"} 
          className="gap-1"
        >
          {gate.overallReady ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
          {gate.overallReady ? "PASS" : "FAIL"}
        </Badge>
      </div>
      
      <div className="space-y-1">
        {gate.checks.map((check, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            {check.passed ? (
              <CheckCircle2 className="h-3 w-3 text-primary" />
            ) : (
              <XCircle className="h-3 w-3 text-destructive" />
            )}
            <span className={check.passed ? "" : "text-destructive"}>{check.label}</span>
          </div>
        ))}
      </div>
      
      {gate.deferredItems.length > 0 && (
        <div className="pt-2 border-t border-border">
          <p className="text-xs text-muted-foreground mb-1">Known Deferrals:</p>
          {gate.deferredItems.map((d, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
              <AlertTriangle className="h-3 w-3" />
              <span>{d}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ================================================================
// MAIN COMPONENT
// ================================================================

export function RolloutStrategyCard() {
  const [config, setConfig] = useState<DealCardV2RolloutConfig>(DEFAULT_ROLLOUT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [localPercent, setLocalPercent] = useState(0);
  const [localAllowlist, setLocalAllowlist] = useState("");
  
  // Load config
  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const fetched = await fetchRolloutConfig();
      setConfig(fetched);
      setLocalPercent(fetched.percent);
      setLocalAllowlist(fetched.allowlist.join(", "));
    } catch (err) {
      console.error("Failed to load rollout config:", err);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);
  
  // Save helpers
  const saveConfig = async (updates: Partial<DealCardV2RolloutConfig>) => {
    setSaving(true);
    const result = await updateRolloutConfig(updates);
    if (result.success) {
      toast.success("Rollout config updated");
      await loadConfig();
    } else {
      toast.error(`Failed to update: ${result.error}`);
    }
    setSaving(false);
  };
  
  const handleEnabledToggle = (enabled: boolean) => {
    saveConfig({ enabled });
  };
  
  const handleEmergencyOff = async () => {
    setSaving(true);
    const result = await updateRolloutConfig({ enabled: false, mode: "off" });
    if (result.success) {
      toast.success("🚨 Emergency OFF activated - All users now on V1");
    } else {
      toast.error(`Failed to activate emergency off: ${result.error}`);
    }
    await loadConfig();
    setSaving(false);
  };
  
  const handleModeChange = (mode: RolloutMode) => {
    saveConfig({ mode });
  };
  
  const handlePercentSave = () => {
    saveConfig({ percent: localPercent });
  };
  
  const handleAllowlistSave = () => {
    const allowlist = localAllowlist
      .split(",")
      .map(s => s.trim())
      .filter(Boolean);
    saveConfig({ allowlist });
  };
  
  const handleRampStep = (mode: RolloutMode, percent?: number) => {
    saveConfig({ mode, ...(percent !== undefined ? { percent } : {}) });
  };
  
  // Status badge
  const getStatusBadge = () => {
    if (!config.enabled) {
      return <Badge variant="secondary" className="gap-1"><XCircle className="h-3 w-3" /> OFF</Badge>;
    }
    if (config.mode === "on") {
      return <Badge className="gap-1 bg-primary"><CheckCircle2 className="h-3 w-3" /> ON</Badge>;
    }
    return <Badge variant="outline" className="gap-1 border-accent text-accent-foreground"><AlertTriangle className="h-3 w-3" /> CANARY</Badge>;
  };
  
  // Preview links
  const baseUrl = window.location.origin;
  const v2PreviewUrl = `${baseUrl}/?dealCardV2=1`;
  const compareUrl = `${baseUrl}/?dealCardV2=1&compare=1`;
  const debugUrl = `${baseUrl}/?dealCardV2=1&dealCardV2debug=1`;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Rocket className="h-5 w-5" />
          Rollout Strategy
        </CardTitle>
        <CardDescription>
          Manage production feature rollouts with staged exposure and monitoring
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* A) Rollout Overview */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Info className="h-4 w-4 text-muted-foreground" />
            Overview
          </div>
          <div className="text-xs text-muted-foreground space-y-1 bg-muted/30 p-3 rounded-lg">
            <p>
              This section controls production feature rollouts using a graduated exposure strategy.
              Features can be rolled out via allowlist (specific users), percent (random sessions), or full-on.
            </p>
            <p className="pt-1">
              <strong>Scope:</strong> Deal Card V2 renderer, Layout Studio, details rendering
            </p>
          </div>
        </div>
        
        <Separator />
        
        {/* B) Current Rollout Status */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium">
              <ToggleRight className="h-4 w-4 text-muted-foreground" />
              Current Status
            </div>
            {getStatusBadge()}
          </div>
          
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <RefreshCw className="h-4 w-4 animate-spin" />
              Loading config...
            </div>
          ) : (
            <div className="space-y-4">
              {/* Main toggle */}
              <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div>
                  <Label className="font-medium">V2 Enabled</Label>
                  <p className="text-xs text-muted-foreground">Master switch for V2 renderer</p>
                </div>
                <Switch
                  checked={config.enabled}
                  onCheckedChange={handleEnabledToggle}
                  disabled={saving}
                />
              </div>
              
              {/* Mode selector */}
              {config.enabled && (
                <div className="space-y-2">
                  <Label className="text-xs">Rollout Mode</Label>
                  <Select
                    value={config.mode}
                    onValueChange={(v) => handleModeChange(v as RolloutMode)}
                    disabled={saving}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="off">
                        <div className="flex items-center gap-2">
                          <Power className="h-3.5 w-3.5" />
                          <span>Off</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="allowlist">
                        <div className="flex items-center gap-2">
                          <Users className="h-3.5 w-3.5" />
                          <span>Allowlist (specific users)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="percent">
                        <div className="flex items-center gap-2">
                          <Percent className="h-3.5 w-3.5" />
                          <span>Percent (gradual rollout)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="on">
                        <div className="flex items-center gap-2">
                          <ToggleRight className="h-3.5 w-3.5" />
                          <span>On (everyone)</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Mode-specific config */}
              {config.enabled && config.mode === "percent" && (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={localPercent}
                    onChange={(e) => setLocalPercent(Number(e.target.value))}
                    className="w-20 h-8"
                    disabled={saving}
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handlePercentSave}
                    disabled={saving || localPercent === config.percent}
                    className="h-8"
                  >
                    Save
                  </Button>
                </div>
              )}
              
              {config.enabled && config.mode === "allowlist" && (
                <div className="space-y-2">
                  <Input
                    placeholder="session-id, user-id, CURRENT_SESSION..."
                    value={localAllowlist}
                    onChange={(e) => setLocalAllowlist(e.target.value)}
                    className="h-8 text-sm"
                    disabled={saving}
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      {config.allowlist.length} identifier(s) in allowlist
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleAllowlistSave}
                      disabled={saving}
                      className="h-7"
                    >
                      Save
                    </Button>
                  </div>
                  <div className="p-2 bg-muted/50 rounded text-xs space-y-1">
                    <p className="font-medium">Your session ID:</p>
                    <code className="text-[10px] block break-all bg-background p-1 rounded border">
                      {getSessionId()}
                    </code>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 text-xs w-full"
                      onClick={() => {
                        const sessionId = getSessionId();
                        const current = localAllowlist ? localAllowlist.split(",").map(s => s.trim()).filter(Boolean) : [];
                        if (!current.includes(sessionId)) {
                          setLocalAllowlist([...current, sessionId].join(", "));
                        }
                      }}
                    >
                      + Add my session to allowlist
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Percent mode - show bucket info */}
              {config.enabled && config.mode === "percent" && (
                <div className="p-2 bg-muted/50 rounded text-xs">
                  <p>Your session bucket: <strong>{getSessionBucket()}</strong>/99</p>
                  <p className="text-muted-foreground">
                    {getSessionBucket() < config.percent ? "✓ You will see V2" : "✗ You will see V1"}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        
        <Separator />
        
        {/* Accordion sections */}
        <Accordion type="multiple" className="w-full">
          {/* C) Suggested Ramp Steps */}
          <AccordionItem value="ramp-steps">
            <AccordionTrigger className="text-sm">
              <div className="flex items-center gap-2">
                <ListChecks className="h-4 w-4 text-muted-foreground" />
                Suggested Ramp Steps
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <RampChecklist
                currentMode={config.mode}
                currentPercent={config.percent}
                enabled={config.enabled}
                onSetMode={handleRampStep}
                saving={saving}
              />
            </AccordionContent>
          </AccordionItem>
          
          {/* Readiness Gate */}
          <AccordionItem value="readiness-gate">
            <AccordionTrigger className="text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                Readiness Gate
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <ReadinessGateDisplay />
            </AccordionContent>
          </AccordionItem>
          
          {/* E) Notes for future rollouts */}
          <AccordionItem value="notes">
            <AccordionTrigger className="text-sm">
              <div className="flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-muted-foreground" />
                Rollout Template Notes
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="text-xs text-muted-foreground space-y-2">
                <p>
                  This rollout strategy pattern can be reused for future production rollouts.
                  Key principles:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Start with allowlist (internal testers only)</li>
                  <li>Graduate through percent-based exposure (1% → 5% → 10% → 25% → 50%)</li>
                  <li>Monitor telemetry and errors at each step</li>
                  <li>Keep Emergency OFF button accessible</li>
                  <li>Full enable (100%) only after all validation passes</li>
                </ul>
                <p className="pt-2 italic">
                  Extend this component for new features by adding feature-specific readiness checks
                  and validation steps.
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        
        <Separator />
        
        {/* D) Quick Actions */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Layers className="h-4 w-4 text-muted-foreground" />
            Quick Actions
          </div>
          
          <div className="flex flex-wrap gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs"
                    onClick={() => window.open(v2PreviewUrl, "_blank")}
                  >
                    <Eye className="h-3.5 w-3.5 mr-1.5" />
                    V2 Preview
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Open with ?dealCardV2=1</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs"
                    onClick={() => window.open(compareUrl, "_blank")}
                  >
                    <LayoutGrid className="h-3.5 w-3.5 mr-1.5" />
                    Compare
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Side-by-side V1/V2 comparison</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs"
                    onClick={() => window.open(debugUrl, "_blank")}
                  >
                    <Bug className="h-3.5 w-3.5 mr-1.5" />
                    Debug
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>V2 debug mode with inspector</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          
          {/* Emergency OFF - always visible */}
          {config.enabled && (
            <div className="pt-2">
              <Button
                size="sm"
                variant="destructive"
                onClick={handleEmergencyOff}
                disabled={saving}
                className="w-full h-9 gap-2"
              >
                <Power className="h-4 w-4" />
                Emergency OFF
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-1">
                Instantly reverts all users to V1
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
